import React, { useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import MetisMenu from "@metismenu/react";
import { useSelector } from "react-redux";
import { AllowedTo } from "react-abac";
import { Permission } from "../../../abac.config";
/* Icons */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTruckPickup,
  faCalculator,
  faUsers,
  faTools,
  faWarehouse,
  faCalendarAlt,
  faPeopleRoof,
  faNetworkWired
} from "@fortawesome/free-solid-svg-icons";
import { MdOutlineEngineering, MdContactPhone, MdForklift } from "react-icons/md";
import { TbCarCrane } from "react-icons/tb";
import { BiSolidFactory } from "react-icons/bi";
import { FaChartColumn } from "react-icons/fa6";
/* Permission Utils */
import {
  canAccessHumanResource,
  canAccessEstimation,
  canAccessRentalEquipment,
  denyChartAndListing,
  denyShopPersonal,
  denyReceiving,
  denyTransport,
  denyMaintenance,
  denyInventory,
  denyFieldCrew,
  canAccessMasterData
} from "../utils/permissionUtils";

const LeftBar = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const roleKey = useSelector((state) => state.loginUserData.roleKey);

  const [toggle, setToggle] = useState(false);

  /* ---------- Permission Flags ---------- */
  const isHumanPageAccess = canAccessHumanResource(roleKey);
  const estimationAccess = canAccessEstimation(roleKey);
  const allowRentalEquipment = canAccessRentalEquipment(roleKey);
  const denyCharts = denyChartAndListing(roleKey);
  const denyShop = denyShopPersonal(roleKey);
  const denyRecv = denyReceiving(roleKey);
  const denyTrans = denyTransport(roleKey);
  const denyMaint = denyMaintenance(roleKey);
  const denyInv = denyInventory(roleKey);
  const denyCrew = denyFieldCrew(roleKey);

  const masterAccess = canAccessMasterData(roleKey);

  /* ---------- Callbacks ---------- */
  const handleDropDown = useCallback((e) => {
    e.preventDefault();
    setToggle(prev => !prev);
  }, []);

  /* ---------- Master Data Menu ---------- */
  const renderMasterLinks = useCallback(() => {
    if (!masterAccess) return null;

    return (
      <li>
        <Link to="#" className="has-arrow" onClick={handleDropDown}>
          <FontAwesomeIcon icon={faNetworkWired} />
          <span className="span-spacing">Master Data</span>
        </Link>

        {toggle && (
          <ul>
            <li><Link to="/vendor" className={pathname.includes("vendor") ? "active" : ""}>Vendor</Link></li>
            <li><Link to="/qc" className={pathname.includes("qc") ? "active" : ""}>QC</Link></li>
            <li><Link to="/location" className={pathname.includes("location") ? "active" : ""}>Location</Link></li>
            <li><Link to="/table" className={pathname.includes("table") ? "active" : ""}>Table</Link></li>
            <li><Link to="/shop" className={pathname.startsWith("/shop") ? "active" : ""}>Shop</Link></li>
            <li><Link to="/drawingtype" className={pathname.includes("drawingtype") ? "active" : ""}>Drawing Type</Link></li>
            <li><Link to="/fieldcrew" className={pathname.includes("fieldcrew") ? "active" : ""}>Field Crew</Link></li>
            <li><Link to="/ShopMaintenance" className={pathname.startsWith("/ShopMaintenance") ? "active" : ""}>Shop Maintenance</Link></li>
            <li><Link to="/FieldMaintenance" className={pathname.startsWith("/FieldMaintenance") ? "active" : ""}>Field Maintenance Listing</Link></li>
            <li><Link to="/fieldMaintenancepage" className={pathname.includes("fieldMaintenancepage") ? "active" : ""}>Field Maintenance</Link></li>
            <li><Link to="/gradeList" className={pathname.includes("gradeList") ? "active" : ""}>Grade</Link></li>
            <li><Link to="/colorCodeList" className={pathname.includes("colorCodeList") ? "active" : ""}>Color Code</Link></li>
            <li><Link to="/certificateList" className={pathname.includes("certificateList") ? "active" : ""}>Certificate List</Link></li>
            <li><Link to="/shopDesignation" className={pathname.includes("shopDesignation") ? "active" : ""}>Shop Designation</Link></li>
            <li><Link to="/title" className={pathname.includes("title") ? "active" : ""}>Title</Link></li>
            <li><Link to="/equipmentList" className={pathname.includes("equipmentList") ? "active" : ""}>Equipment List</Link></li>
            <li><Link to="/projectTypeList" className={pathname.includes("projectTypeList") ? "active" : ""}>Project Type</Link></li>
            <li><Link to="/generalContractorList" className={pathname.includes("generalContractorList") ? "active" : ""}>General Contractor</Link></li>
          </ul>
        )}
      </li>
    );
  }, [handleDropDown, toggle, pathname, masterAccess]);

  return (
    <div className="vertical-menu mm-active" id="navbarToggle">
      <div className="navbar-brand-box" style={{ textAlign: 'center', lineHeight: '70px' }}>
        <a class="logo logo-dark" style={{ display: 'inline-block' }}>
          <span class="logo-sm">
            <img src="/assets/images/logo.png" alt="" height="42" />
          </span>
          <span class="logo-lg">
            <img src="/assets/images/logo.png" alt="" style={{ height: '60px', objectFit: 'contain', verticalAlign: 'middle' }} />
          </span>
        </a>
      </div>

      <div data-simplebar="init" className="sidebar-menu-scroll">
        <div
          className="simplebar-wrapper"
          style={{ margin: "0px", height: "100%", overflow: "scroll" }}
        >
          <div id="sidebar-menu" className="mm-active">
            <MetisMenu>

              {roleKey !== "user" && (
                <>
                  <li>
                    <Link to="/dashboard" className={pathname.includes("dashboard") ? "active" : ""}>
                      <i className="uil uil-clipboard"></i>
                      <span>Dashboard</span>
                    </Link>
                  </li>

                  <li>
                    <Link to="/project" className={pathname.startsWith("/project") ? "active" : ""} onClick={() => localStorage.removeItem("dashboardConstants")}>
                      <i className="uil uil-clipboard"></i>
                      <span>Projects</span>
                    </Link>
                  </li>
                </>
              )}
              <li>
                {roleKey === "superadmin" || roleKey === "admin" || roleKey === "projectmanager" ?
                  <Link
                    to="/archive"
                    className={pathname.includes('archive') ? "active" : ""}
                  >
                    <i className="uil uil-clipboard"></i>
                    <span>Project Archive</span>
                  </Link> : ""
                }
              </li>
              {!denyCharts && (
                <>
                  <li>
                    <Link to="/chartsProject" className={pathname.includes("chartsProject") ? "active" : ""}>
                      <FaChartColumn />
                      <span className="span-spacing">Project Chart</span>
                    </Link>
                  </li>

                  <li>
                    <Link to="/listingProjects" className={pathname.includes("listingProjects") ? "active" : ""} onClick={() => localStorage.removeItem("dashboardConstants")}>
                      <i className="uil uil-clipboard"></i>
                      <span>Project Listing</span>
                    </Link>
                  </li>
                </>
              )}

              {!denyTrans && (
                <li>
                  <Link to="/transport" className={pathname.includes("transport") ? "active" : ""}>
                    <FontAwesomeIcon icon={faTruckPickup} />
                    <span className="span-spacing">Transport</span>
                  </Link>
                </li>
              )}

              {allowRentalEquipment && (
                <li>
                  <Link to="/rentalEquipment" className={pathname.includes("rentalEquipment") ? "active" : ""}>
                    <TbCarCrane />
                    <span className="span-spacing">Rental Equipment</span>
                  </Link>
                </li>
              )}

              {estimationAccess && (
                <li>
                  <Link to="/estimation" className={pathname.includes("estimation") ? "active" : ""}>
                    <FontAwesomeIcon icon={faCalculator} />
                    <span className="span-spacing">Estimation</span>
                  </Link>
                </li>
              )}

              {isHumanPageAccess && (
                <>
                  <li>
                    <Link to="/humanResource" className={pathname.includes("humanResource") ? "active" : ""}>
                      <FontAwesomeIcon icon={faUsers} />
                      <span className="span-spacing">Human Resource</span>
                    </Link>
                  </li>

                  <li>
                    <Link to="/employeeContact" className={pathname.includes("employeeContact") ? "active" : ""}>
                      <MdContactPhone />
                      <span className="span-spacing">Employee Contact</span>
                    </Link>
                  </li>
                </>
              )}

              {!denyShop && (
                <li>
                  <Link to="/shopPersonal" className={pathname.includes("shopPersonal") ? "active" : ""}>
                    <BiSolidFactory />
                    <span className="span-spacing">Shop Personal</span>
                  </Link>
                </li>
              )}

              {!denyRecv && (
                <li>
                  <Link to="/receiving" className={pathname.includes("receiving") ? "active" : ""}>
                    <MdForklift />
                    <span className="span-spacing">Receiving</span>
                  </Link>
                </li>
              )}

              {!denyMaint && (
                <li>
                  <Link to="/maintenance" className={pathname.includes("maintenance") ? "active" : ""}>
                    <FontAwesomeIcon icon={faTools} />
                    <span className="span-spacing">Maintenance</span>
                  </Link>
                </li>
              )}

              {!denyInv && (
                <li>
                  <Link to="/inventory" className={pathname.includes("inventory") ? "active" : ""}>
                    <FontAwesomeIcon icon={faWarehouse} />
                    <span className="span-spacing">Inventory</span>
                  </Link>
                </li>
              )}

              <li>
                {roleKey === "superadmin" || roleKey === "admin" || roleKey === "projectmanager" ?
                  <Link to="/timeOffcalendar" className={pathname.includes("timeOffcalendar") ? "active" : ""}>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <span className="span-spacing">TimeOff Calendar</span>
                  </Link> : ""
                }
              </li>

              {!denyCrew && (
                <li>
                  <Link to="/fieldcrewcalender" className={pathname.includes("fieldcrewcalender") ? "active" : ""}>
                    <MdOutlineEngineering />
                    <span className="span-spacing">Field Crews</span>
                  </Link>
                </li>
              )}

              <AllowedTo perform={Permission.USER_WRITE}>
                <li>
                  <Link to="/user" className={pathname.includes("user") ? "active" : ""}>
                    <FontAwesomeIcon icon={faPeopleRoof} />
                    <span className="span-spacing">User Management</span>
                  </Link>
                </li>
              </AllowedTo>

              {renderMasterLinks()}

            </MetisMenu>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeftBar;
